import React, { useState, useEffect } from "react";
import {
	Modal,
	Form,
	Input,
	Select,
	DatePicker,
	Button,
	Table,
	Tag,
	message,
	Row,
	Col,
	Typography,
	Space,
	Divider,
	Card,
	Popconfirm,
} from "antd";
import {
	UserOutlined,
	PlusOutlined,
	HeartOutlined,
	UserAddOutlined,
	DeleteOutlined,
} from "@ant-design/icons";
import useAxios from "../../utilities/useAxios";
import dayjs from "dayjs";
import useIsMobile from "../../hooks/useIsMobile";

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

const EditFamilyRelationshipModal = ({
	visible,
	onCancel,
	onSuccess,
	userId,
	userName,
}) => {
	const isMobile = useIsMobile();
	const [loading, setLoading] = useState(false);
	const [members, setMembers] = useState([]);
	const [selectedMembers, setSelectedMembers] = useState([]);
	const [searchText, setSearchText] = useState("");
	const [searchCategory, setSearchCategory] = useState("name");
	const [relationshipType, setRelationshipType] = useState("부모");
	const [showCreateForm, setShowCreateForm] = useState(false);
	const [createForm] = Form.useForm();

	// New state for current relationships
	const [currentRelationships, setCurrentRelationships] = useState({
		parents: [],
		children: [],
		spouse: [],
		other: [],
	});
	const [loadingRelationships, setLoadingRelationships] = useState(false);

	useEffect(() => {
		if (visible) {
			fetchMembers();
			fetchCurrentRelationships();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- run only when visible
	}, [visible]);

	const fetchMembers = async () => {
		try {
			// Fetch all users including inactive ones
			const response = await useAxios.get(
				"/users/all-users/",
				{
					params: {
						include_inactive: "true",
					},
				}
			);
			if (response.status === 200) {
				setMembers(response.data);
			}
		} catch (error) {
			message.error("회원 목록을 불러오는데 실패했습니다.");
		}
	};

	const fetchCurrentRelationships = async () => {
		try {
			setLoadingRelationships(true);
			const response = await useAxios.get(
				"/users/family-relationships/",
				{
					params: {
						user_id: userId,
					},
				}
			);
			if (response.status === 200) {
				setCurrentRelationships(response.data);
			}
		} catch (error) {
			message.error(
				"현재 가족 관계를 불러오는데 실패했습니다."
			);
		} finally {
			setLoadingRelationships(false);
		}
	};

	const handleSearch = (value) => {
		setSearchText(value);
	};

	const handleCategoryChange = (value) => {
		setSearchCategory(value);
	};

	const handleRelationshipTypeChange = (value) => {
		setRelationshipType(value);
	};

	const handleMemberSelection = (selectedRowKeys, selectedRows) => {
		// Get all currently selected members (including those not in current filtered view)
		const allSelectedMembers = [...selectedMembers];

		// Update the selection for the current filtered members
		selectedRows.forEach((selectedRow) => {
			const existingIndex = allSelectedMembers.findIndex(
				(member) => member.id === selectedRow.id
			);
			if (existingIndex === -1) {
				// Add new selection
				allSelectedMembers.push(selectedRow);
			}
		});

		// Remove deselected members from current filtered view
		const currentFilteredIds = filteredMembers.map(
			(member) => member.id
		);
		const finalSelectedMembers = allSelectedMembers.filter(
			(member) => {
				// Keep if it's in the current filtered view and selected
				if (currentFilteredIds.includes(member.id)) {
					return selectedRowKeys.includes(
						member.id
					);
				}
				// Keep if it's not in current filtered view (preserve previous selections)
				return true;
			}
		);

		setSelectedMembers(finalSelectedMembers);
	};

	const handleCreateNewMember = async (values) => {
		try {
			setLoading(true);
			const submitData = {
				...values,
				birthdate: values.birthdate
					? values.birthdate.format("YYYY-MM-DD")
					: null,
				is_active: false, // Create as inactive member
			};

			const response = await useAxios.post(
				"/users/add-member/",
				submitData
			);

			if (response.status === 201) {
				message.success(
					"새 회원이 성공적으로 생성되었습니다."
				);
				// Add the new member to the list and select it
				const newMember = response.data;
				setMembers([...members, newMember]);
				setSelectedMembers([
					...selectedMembers,
					newMember,
				]);
				setShowCreateForm(false);
				createForm.resetFields();
				// Refresh current relationships after creating new member
				fetchCurrentRelationships();
			}
		} catch (error) {
			message.error("새 회원 생성에 실패했습니다.");
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async () => {
		if (selectedMembers.length === 0) {
			message.error("최소 한 명의 회원을 선택해주세요.");
			return;
		}

		try {
			setLoading(true);

			// Create family relationships for each selected member
			for (const member of selectedMembers) {
				await useAxios.post(
					"/users/family-relationships/",
					{
						user_id: userId,
						related_user_id: member.id,
						relationship: relationshipType,
					}
				);
			}

			// Show appropriate success message based on relationship type
			let successMessage =
				"가족 관계가 성공적으로 추가되었습니다.";
			if (selectedMembers.length > 1) {
				successMessage = `${selectedMembers.length}명의 회원과의 가족 관계가 성공적으로 추가되었습니다.`;
			}

			// Add relationship type specific message
			const relationshipLabels = {
				부모: "부모",
				자녀: "자녀",
				배우자: "배우자",
				기타: "기타",
			};

			const relationshipLabel =
				relationshipLabels[relationshipType] ||
				relationshipType;
			successMessage += ` (${relationshipLabel} 관계가 양방향으로 생성되었습니다)`;

			message.success(successMessage);
			if (onSuccess) {
				onSuccess();
			}
			// Refresh current relationships after adding new ones
			fetchCurrentRelationships();
		} catch (error) {
			if (error.response?.data?.relationship) {
				// Handle specific relationship errors
				console.log(error.response?.data?.relationship);
				message.error("이미 존재하는 가족 관계입니다.");
			} else {
				message.error("가족 관계 추가에 실패했습니다.");
			}
		} finally {
			setLoading(false);
		}
	};

	const handleRemoveRelationship = async (
		relatedUserId,
		relationshipType
	) => {
		try {
			setLoading(true);
			const response = await useAxios.delete(
				"/users/family-relationships/",
				{
					data: {
						user_id: userId,
						related_user_id: relatedUserId,
						relationship: relationshipType,
					},
				}
			);

			if (response.status === 204) {
				message.success(
					"가족 관계가 성공적으로 제거되었습니다."
				);
				fetchCurrentRelationships(); // Refresh the relationships
				if (onSuccess) {
					onSuccess();
				}
			}
		} catch (error) {
			message.error("가족 관계 제거에 실패했습니다.");
		} finally {
			setLoading(false);
		}
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "입교인":
				return "blue";
			case "학습인":
				return "green";
			case "유아세례교인":
				return "orange";
			case "실종교인":
				return "red";
			case "별세교인":
				return "default";
			default:
				return "default";
		}
	};

	const getClassificationColor = (classification) => {
		switch (classification) {
			case "성도":
				return "blue";
			case "교직원":
				return "green";
			case "교역자":
				return "purple";
			case "은퇴목사":
				return "orange";
			default:
				return "default";
		}
	};

	const filteredMembers = members.filter((member) => {
		// Exclude the current user from the list
		if (member.id === userId) {
			return false;
		}

		if (searchText) {
			const searchValue =
				member[searchCategory]
					?.toString()
					.toLowerCase() || "";
			if (!searchValue.includes(searchText.toLowerCase())) {
				return false;
			}
		}
		return true;
	});

	const memberColumns = [
		{
			title: "이름",
			dataIndex: "name",
			key: "name",
			render: (text) => text || "-",
		},
		{
			title: "생년월일",
			dataIndex: "birthdate",
			key: "birthdate",
			render: (text) =>
				text ? dayjs(text).format("YYYY-MM-DD") : "-",
		},
		{
			title: "성별",
			dataIndex: "gender",
			key: "gender",
			render: (text) => text || "-",
		},
		{
			title: "분류",
			dataIndex: "classification_name",
			key: "classification_name",
			render: (text) => (
				<Tag color={getClassificationColor(text)}>
					{text || "-"}
				</Tag>
			),
		},
		{
			title: "상태",
			dataIndex: "status_name",
			key: "status_name",
			render: (text) => (
				<Tag color={getStatusColor(text)}>
					{text || "-"}
				</Tag>
			),
		},
		{
			title: "활성상태",
			dataIndex: "is_active",
			key: "is_active",
			render: (isActive) => (
				<Tag color={isActive ? "green" : "red"}>
					{isActive ? "활성" : "비활성"}
				</Tag>
			),
		},
		{
			title: "소속",
			dataIndex: "affiliated_group",
			key: "affiliated_group",
			render: (text) => text || "-",
		},
	];

	const relationshipOptions = [
		{ value: "부모", label: "부모" },
		{ value: "자녀", label: "자녀" },
		{ value: "배우자", label: "배우자" },
		{ value: "기타", label: "기타" },
	];

	return (
		<Modal
			title={
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "8px",
					}}>
					<HeartOutlined />
					<span>가족 관계 편집 - {userName}</span>
				</div>
			}
			open={visible}
			onCancel={onCancel}
			footer={null}
			width="90%"
			style={{ top: 20 }}>
			{/* Relationship Type Selection */}
			<Card size="small" style={{ marginBottom: 16 }}>
				<Row gutter={[16, 16]} align="middle">
					<Col xs={24} sm={12} md={6}>
						<Text strong>관계 유형:</Text>
					</Col>
					<Col xs={24} sm={12} md={6}>
						<Select
							value={relationshipType}
							onChange={
								handleRelationshipTypeChange
							}
							style={{
								width: "100%",
							}}
							options={
								relationshipOptions
							}
						/>
					</Col>
					<Col xs={24} sm={12} md={6}>
						<Button
							type="primary"
							icon={
								<UserAddOutlined />
							}
							onClick={() =>
								setShowCreateForm(
									true
								)
							}
							style={{
								width: "100%",
							}}>
							새 회원 생성
						</Button>
					</Col>
				</Row>
				<Row style={{ marginTop: 8 }}>
					<Col span={24}>
						<Text
							type="secondary"
							style={{
								fontSize: "12px",
							}}>
							💡 가족 관계는
							양방향으로 생성됩니다.
							예를 들어, 부모 관계를
							추가하면 자동으로 자녀
							관계도 함께 생성됩니다.
						</Text>
					</Col>
				</Row>
			</Card>

			{/* Current Family Relationships */}
			<Card
				size="small"
				title={
					<Space>
						<HeartOutlined />
						현재 가족 관계
					</Space>
				}
				style={{ marginBottom: 16 }}
				loading={loadingRelationships}>
				{Object.keys(currentRelationships).map(
					(relationshipType) => {
						const relationships =
							currentRelationships[
								relationshipType
							];
						if (relationships.length === 0)
							return null;

						const relationshipLabels = {
							parents: "부모",
							children: "자녀",
							spouse: "배우자",
							other: "기타",
						};

						return (
							<div
								key={
									relationshipType
								}
								style={{
									marginBottom: 16,
								}}>
								<Title
									level={
										5
									}
									style={{
										marginBottom: 8,
									}}>
									{
										relationshipLabels[
											relationshipType
										]
									}{" "}
									(
									{
										relationships.length
									}
									명)
								</Title>
								<Row
									gutter={[
										8,
										8,
									]}>
									{relationships.map(
										(
											member
										) => (
											<Col
												key={
													member.id
												}
												xs={
													24
												}
												sm={
													12
												}
												md={
													8
												}
												lg={
													6
												}>
												<Card
													size="small"
													style={{
														border: "1px solid #d9d9d9",
														position: "relative",
													}}>
													<div
														style={{
															textAlign: "center",
														}}>
														<div
															style={{
																width: 40,
																height: 40,
																borderRadius:
																	"50%",
																backgroundColor:
																	member.gender ===
																	"남"
																		? "#2457e6"
																		: "#eb2f96",
																display: "flex",
																alignItems: "center",
																justifyContent:
																	"center",
																color: "white",
																fontSize: "16px",
																margin: "0 auto 8px",
															}}>
															<UserOutlined />
														</div>
														<div
															style={{
																fontWeight: "bold",
																marginBottom: 4,
															}}>
															{
																member.name
															}
														</div>
														<div
															style={{
																fontSize: "12px",
																color: "#666",
																marginBottom: 4,
															}}>
															{member.birthdate
																? dayjs(
																		member.birthdate
																  ).format(
																		"YYYY-MM-DD"
																  )
																: "-"}
														</div>
														<Tag
															size="small"
															color="blue"
															style={{
																marginBottom: 8,
															}}>
															{
																relationshipLabels[
																	relationshipType
																]
															}
														</Tag>
														<Popconfirm
															title="가족 관계 제거"
															description="이 가족 관계를 제거하시겠습니까?"
															onConfirm={() =>
																handleRemoveRelationship(
																	member.id,
																	member.relationship_type
																)
															}
															okText="제거"
															cancelText="취소">
															<Button
																type="text"
																size="small"
																danger
																icon={
																	<DeleteOutlined />
																}
																style={{
																	position: "absolute",
																	top: 4,
																	right: 4,
																}}>
																제거
															</Button>
														</Popconfirm>
													</div>
												</Card>
											</Col>
										)
									)}
								</Row>
							</div>
						);
					}
				)}

				{Object.values(currentRelationships).every(
					(arr) => arr.length === 0
				) && (
					<div
						style={{
							textAlign: "center",
							padding: "20px",
							color: "#999",
						}}>
						<Text>
							등록된 가족 관계가
							없습니다.
						</Text>
					</div>
				)}
			</Card>

			{/* Add New Family Relationships */}
			<Divider>
				<Title level={4}>
					<PlusOutlined /> 새로운 가족 관계 추가
				</Title>
			</Divider>

			{/* Create New Member Form */}
			{showCreateForm && (
				<Card
					size="small"
					title="새 회원 생성"
					style={{ marginBottom: 16 }}
					extra={
						<Button
							size="small"
							onClick={() =>
								setShowCreateForm(
									false
								)
							}>
							닫기
						</Button>
					}>
					<Form
						form={createForm}
						layout="vertical"
						onFinish={handleCreateNewMember}
						initialValues={{
							gender: "남",
						}}>
						<Row gutter={[16, 16]}>
							<Col
								xs={24}
								sm={12}
								md={6}>
								<Form.Item
									name="name"
									label="이름"
									rules={[
										{
											required: true,
											message: "이름을 입력하세요",
										},
									]}>
									<Input placeholder="이름 입력" />
								</Form.Item>
							</Col>
							<Col
								xs={24}
								sm={12}
								md={6}>
								<Form.Item
									name="gender"
									label="성별"
									rules={[
										{
											required: true,
											message: "성별을 선택하세요",
										},
									]}>
									<Select placeholder="성별 선택">
										<Option value="남">
											남
										</Option>
										<Option value="여">
											여
										</Option>
									</Select>
								</Form.Item>
							</Col>
							<Col
								xs={24}
								sm={12}
								md={6}>
								<Form.Item
									name="birthdate"
									label="생년월일">
									<DatePicker
										inputReadOnly={isMobile}
										style={{
											width: "100%",
										}}
										format="YYYY-MM-DD"
									/>
								</Form.Item>
							</Col>
							<Col
								xs={24}
								sm={12}
								md={6}>
								<Form.Item
									name="phone_number"
									label="연락처">
									<Input placeholder="연락처 입력" />
								</Form.Item>
							</Col>
							<Col
								xs={24}
								sm={12}
								md={6}>
								<Form.Item
									name="address"
									label="주소">
									<Input placeholder="주소 입력" />
								</Form.Item>
							</Col>
							<Col
								xs={24}
								sm={12}
								md={6}>
								<Form.Item
									name="address_detail"
									label="상세주소">
									<Input placeholder="상세주소 입력" />
								</Form.Item>
							</Col>
							<Col
								xs={24}
								sm={12}
								md={6}>
								<Form.Item
									name="job"
									label="직업">
									<Input placeholder="직업 입력" />
								</Form.Item>
							</Col>
						</Row>
						<Form.Item>
							<Button
								type="primary"
								htmlType="submit"
								loading={
									loading
								}
								icon={
									<PlusOutlined />
								}>
								회원 생성
							</Button>
						</Form.Item>
					</Form>
				</Card>
			)}

			{/* Member Search and Selection */}
			<div style={{ marginBottom: 16 }}>
				<Title level={4}>
					<UserOutlined /> 회원 선택 (새 가족 관계
					추가)
				</Title>
				<Text type="secondary">
					가족 관계로 추가할 회원을 선택하세요.
				</Text>

				{/* Search Section */}
				<Row
					gutter={[16, 16]}
					style={{
						marginTop: 16,
						marginBottom: 16,
					}}>
					<Col xs={24} sm={12} md={6}>
						<Select
							defaultValue="name"
							style={{
								width: "100%",
							}}
							onChange={
								handleCategoryChange
							}
							options={[
								{
									value: "name",
									label: "이름",
								},
								{
									value: "phone_number",
									label: "연락처",
								},
								{
									value: "affiliated_group",
									label: "소속",
								},
							]}
						/>
					</Col>
					<Col xs={24} sm={12} md={6}>
						<Input.Search
							placeholder="검색어를 입력하세요"
							allowClear
							onSearch={handleSearch}
							style={{
								width: "100%",
							}}
						/>
					</Col>
				</Row>

				{/* Member Table */}
				<Table
					columns={memberColumns}
					dataSource={filteredMembers}
					rowKey="id"
					rowSelection={{
						type: "checkbox",
						selectedRowKeys:
							selectedMembers.map(
								(member) =>
									member.id
							),
						onChange: handleMemberSelection,
					}}
					pagination={{
						pageSize: 10,
						showSizeChanger: true,
						showQuickJumper: true,
					}}
					scroll={{ y: 300 }}
				/>

				{selectedMembers.length > 0 && (
					<div style={{ marginTop: 16 }}>
						<Text strong>
							선택된 회원:{" "}
							{selectedMembers.length}
							명
						</Text>
						<div style={{ marginTop: 8 }}>
							{selectedMembers.map(
								(member) => (
									<Tag
										key={
											member.id
										}
										style={{
											marginBottom: 4,
										}}>
										{
											member.name
										}
									</Tag>
								)
							)}
						</div>
					</div>
				)}
			</div>

			{/* Submit Button */}
			<div style={{ textAlign: "right" }}>
				<Button
					type="primary"
					onClick={handleSubmit}
					loading={loading}
					icon={<HeartOutlined />}
					disabled={selectedMembers.length === 0}>
					가족 관계 추가
				</Button>
			</div>
		</Modal>
	);
};

export default EditFamilyRelationshipModal;
